import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlarmClock, Plus, Check, Trash2, Loader2 } from "lucide-react";
import { ReminderDialog } from "./ReminderDialog";
import { remindersApi, fmtDue } from "./remindersApi";

const STATE_LABEL = { overdue: "scaduto", today: "oggi" };

// Header icon (next to the notifications bell): badge = reminders due today/overdue, dropdown = upcoming list.
export function RemindersBell() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  const ref = useRef(null);

  const load = () => remindersApi.list().then(setRows).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 5 * 60 * 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const done = async (r) => {
    setBusy(r.id);
    try { await remindersApi.patch(r.id, { done: true }); setRows((x) => x.filter((y) => y.id !== r.id)); toast.success("Fatto"); }
    catch { toast.error("Errore"); } finally { setBusy(null); }
  };
  const remove = async (r) => {
    if (!window.confirm("Eliminare il promemoria?")) return;
    setBusy(r.id);
    try { await remindersApi.remove(r.id); setRows((x) => x.filter((y) => y.id !== r.id)); }
    catch { toast.error("Errore"); } finally { setBusy(null); }
  };

  const due = rows.filter((r) => r.state !== "upcoming").length;
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} aria-label="Promemoria" data-testid="reminders-bell"
        className="relative inline-flex items-center justify-center rounded-full border border-[#f7ebdf]/30 h-9 w-9 hover:bg-[#f7ebdf]/10">
        <AlarmClock className={`h-4 w-4 ${due ? "animate-pulse text-amber-300" : ""}`} />
        {due > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-amber-400 text-[#2b1410] text-[10px] font-bold flex items-center justify-center px-1" data-testid="reminders-badge">{due}</span>}
        {due === 0 && rows.length > 0 && <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[#c5a059] border border-[#2b1410]" data-testid="reminders-dot" />}
      </button>

      {open && (
        <div className="fixed left-2 right-2 top-14 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 max-h-[80vh] rounded-xl bg-white text-[#2b1410] border border-[#e8dcc8] shadow-xl overflow-hidden z-[10000] flex flex-col" data-testid="reminders-dropdown">
          <div className="p-3 border-b border-[#e8dcc8] flex items-center gap-2">
            <div className="font-serif font-bold text-sm flex-1">Promemoria{rows.length > 0 && <span className="ml-1 text-xs font-sans font-normal text-[#7c5a4e]">· {rows.length} aperti</span>}</div>
            <button onClick={() => { setAdding(true); setOpen(false); }} className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] text-[#f7ebdf] px-3 py-1 text-xs font-semibold hover:bg-[#2b1410]" data-testid="reminder-add-btn">
              <Plus className="h-3.5 w-3.5" /> Nuovo
            </button>
          </div>
          {rows.length === 0 ? <p className="p-4 text-xs text-[#9a7f72]" data-testid="reminders-empty">Nessun promemoria. Segnati scadenze, opzioni camere, richiami: il giorno giusto ti avvisiamo qui e sul telefono.</p>
            : <ul className="divide-y divide-[#f0e6d8] overflow-y-auto" data-testid="reminders-list">
              {rows.map((r) => (
                <li key={r.id} className={`px-3 py-2 flex items-start gap-2.5 border-l-4 ${r.state === "upcoming" ? "border-transparent" : "border-amber-400 bg-amber-50"}`} data-testid={`reminder-row-${r.id}`} data-state={r.state}>
                  <button disabled={busy === r.id} onClick={() => done(r)} title="Segna come fatto" data-testid={`reminder-done-${r.id}`}
                    className="mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 border-[#4a1c17] text-[#4a1c17] hover:bg-[#4a1c17] hover:text-[#f7ebdf] inline-flex items-center justify-center">
                    {busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm break-words">{r.text}</div>
                    <div className="text-[11px] text-[#7c5a4e] mt-0.5">
                      {fmtDue(r)}{STATE_LABEL[r.state] && <span className="ml-2 rounded-full bg-amber-200 text-amber-900 px-2 py-0.5 font-semibold uppercase text-[10px]">{STATE_LABEL[r.state]}</span>}
                      {r.ref_name && <span className="ml-2">· {r.ref_name}</span>}
                    </div>
                  </div>
                  <button disabled={busy === r.id} onClick={() => remove(r)} title="Elimina" className="p-1 rounded-md text-[#9a7f72] hover:bg-[#f7ebdf] hover:text-red-700" data-testid={`reminder-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>}
        </div>
      )}
      {adding && <ReminderDialog open onClose={() => setAdding(false)} onCreated={load} />}
    </div>
  );
}
